export type BrandConfig = {
  key: string;
  appTitle: string;
  displayName: string;
  certificateLogoSrc?: string;
  supportBaseUrl?: string;
};

export type BrandVisibilityRule = {
  showForBrands?: string[];
  hideForBrands?: string[];
};

export type BrandFeatures = {
  intercomEnabled: boolean;
  faceIdIdentificationOverlayEnabled: boolean;
  biometricResetEnabled: boolean;
  biometricConsentRequired: boolean;
};

const DEFAULT_BRAND_KEY = "trustexam";
const SYSTEMS247_BRAND_KEY = "systems247";
const BRAND_KEY_ALIASES: Record<string, string> = {
  trustexam: DEFAULT_BRAND_KEY,
  trustexamai: DEFAULT_BRAND_KEY,
  examru: "examru",
  demo: "demo",
  system247: SYSTEMS247_BRAND_KEY,
  systems247: SYSTEMS247_BRAND_KEY,
};

const DEFAULT_BRAND_FEATURES: BrandFeatures = {
  intercomEnabled: true,
  faceIdIdentificationOverlayEnabled: true,
  biometricResetEnabled: false,
  biometricConsentRequired: false,
};

const BRAND_FEATURES: Record<string, Partial<BrandFeatures>> = {
  trustexam: {
    biometricResetEnabled: true,
    biometricConsentRequired: true,
  },
  examru: {
    intercomEnabled: false,
    faceIdIdentificationOverlayEnabled: false,
    biometricResetEnabled: false,
    biometricConsentRequired: false,
  },
  demo: {
    faceIdIdentificationOverlayEnabled: false,
    biometricResetEnabled: false,
    biometricConsentRequired: false,
  },
  systems247: {
    faceIdIdentificationOverlayEnabled: false,
    biometricResetEnabled: true,
    biometricConsentRequired: true,
  },
};

export const BRANDS: Record<string, BrandConfig> = {
  trustexam: {
    key: "trustexam",
    appTitle: "TrustExam",
    displayName: "TrustExam",
    certificateLogoSrc: "/TrustExam.png",
    supportBaseUrl: "https://support.trustexam.ai",
  },
  examru: {
    key: "examru",
    appTitle: "Честный Экзамен",
    displayName: "Честный Экзамен",
    certificateLogoSrc: "/examru-logo-dark.svg",
    supportBaseUrl: "https://support.trustexam.ai",
  },
  demo: {
    key: "demo",
    appTitle: "Demo",
    displayName: "Demo",
    // NOTE: add real asset if needed
    // certificateLogoSrc: "/demo-logo.png",
    // supportBaseUrl: "https://support.example.com",
  },
};

function normalizeBrandKey(brandKey: string | null | undefined): string {
  const raw = (brandKey ?? "").trim().toLowerCase();

  if (!raw) return DEFAULT_BRAND_KEY;

  return BRAND_KEY_ALIASES[raw] ?? DEFAULT_BRAND_KEY;
}

export function getBrandKeyFromEnv(): string {
  return normalizeBrandKey(
    process.env.NEXT_PUBLIC_BRAND_KEY ?? process.env.BRAND_KEY ?? ""
  );
}

export function getBrandFeatures(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): BrandFeatures {
  const key = normalizeBrandKey(brandKey);

  return {
    ...DEFAULT_BRAND_FEATURES,
    ...(BRAND_FEATURES[key] ?? {}),
  };
}

export function getBrandConfigByKey(brandKey: string | null | undefined): BrandConfig {
  const key = normalizeBrandKey(brandKey);
  return BRANDS[key] ?? BRANDS[DEFAULT_BRAND_KEY];
}

export function getBrandConfig(): BrandConfig {
  return getBrandConfigByKey(getBrandKeyFromEnv());
}

export function isSystems247Brand(): boolean {
  return getBrandKeyFromEnv() === SYSTEMS247_BRAND_KEY;
}

export function getBrandI18nKey(baseKey: string): string {
  const brandKey = getBrandKeyFromEnv();
  const normalized = brandKey === "examru" ? "examru" : "trustexam";
  return `${baseKey}-${normalized}`;
}

export function isIntercomEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).intercomEnabled;
}

export function isFaceIdIdentificationOverlayEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).faceIdIdentificationOverlayEnabled;
}

export function isBiometricResetEnabled(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).biometricResetEnabled;
}

export function isBiometricConsentRequired(
  brandKey: string | null | undefined = getBrandKeyFromEnv()
): boolean {
  return getBrandFeatures(brandKey).biometricConsentRequired;
}

export function isVisibleForBrand(
  rule: BrandVisibilityRule,
  brandKey: string
): boolean {
  const normalizedBrandKey = normalizeBrandKey(brandKey);

  if (rule.showForBrands?.length) {
    return rule.showForBrands.includes(normalizedBrandKey);
  }

  if (rule.hideForBrands?.length) {
    return !rule.hideForBrands.includes(normalizedBrandKey);
  }

  return true;
}
